/**
 * 官方订阅套餐模型（T14）。
 *
 * 纯数据 + 纯函数：不引入任何 UI / DOM / 网络依赖，可在 Node 下直接单测。
 * 三档套餐 —— 文字对话 / 图文对话 / 声图文对话，每档给出一份能力矩阵
 * （text / image / tts / autoVoice），供选择页展示与自动化编排消费。
 *
 * 边界：本文件只描述「能力」与「价格占位」，真实计费 / 权益校验留后续任务。
 */

/** 套餐档位标识（顺序即选择页从上到下的展示顺序）。 */
export type PlanTier = 'text' | 'multimodal' | 'voice';

/** 套餐能力矩阵：这一档能自动完成哪些事。 */
export interface PlanCapabilities {
  /** 自动生文：根据用户输入产出文字回复。 */
  text: boolean;
  /** 自动生图：根据文字内容产出配图。 */
  image: boolean;
  /** TTS：把文字回复朗读成语音。 */
  tts: boolean;
  /** 全自动语音：生文 + 生图 + TTS 一次跑完（声图文套餐的完整闭环）。 */
  autoVoice: boolean;
}

export type PlanCapabilityKey = keyof PlanCapabilities;

/** 能力矩阵的固定枚举顺序，UI 与测试共用，避免各处自定义顺序。 */
export const PLAN_CAPABILITY_KEYS: readonly PlanCapabilityKey[] = [
  'text',
  'image',
  'tts',
  'autoVoice',
];

/** 能力矩阵的中文短名（选择页能力徽标 / 文案）。 */
export const PLAN_CAPABILITY_LABELS: Readonly<Record<PlanCapabilityKey, string>> = {
  text: '自动生文',
  image: '自动生图',
  tts: 'TTS 朗读',
  autoVoice: '全自动语音',
};

/** 一档套餐的完整定义。 */
export interface PlanDefinition {
  id: PlanTier;
  /** 套餐名，如「图文对话套餐」。 */
  name: string;
  /** 一句话卖点。 */
  tagline: string;
  /** 价格占位：真实计费未接入，仅用于版式。 */
  priceLabel: string;
  /** 价格补充说明（如「/ 月 · 占位」）。 */
  priceNote: string;
  /** 卡片上展示的能力要点（中文短句，面向用户）。 */
  highlights: readonly string[];
  /** 能力矩阵（机器可读，编排逻辑据此决定跑哪些步骤）。 */
  capabilities: PlanCapabilities;
}

const TEXT_CAPABILITIES: PlanCapabilities = {
  text: true,
  image: false,
  tts: false,
  autoVoice: false,
};

const MULTIMODAL_CAPABILITIES: PlanCapabilities = {
  text: true,
  image: true,
  tts: false,
  autoVoice: false,
};

const VOICE_CAPABILITIES: PlanCapabilities = {
  text: true,
  image: true,
  tts: true,
  autoVoice: true,
};

const TEXT_PLAN: PlanDefinition = {
  id: 'text',
  name: '文字对话套餐',
  tagline: '纯文字往来，专注内容本身',
  priceLabel: '¥ --',
  priceNote: '/ 月 · 占位',
  highlights: ['自动生文', '不限轮次连续对话', '本地保存对话记录'],
  capabilities: TEXT_CAPABILITIES,
};

const MULTIMODAL_PLAN: PlanDefinition = {
  id: 'multimodal',
  name: '图文对话套餐',
  tagline: '边聊边配图，看图说话',
  priceLabel: '¥ --',
  priceNote: '/ 月 · 占位',
  highlights: ['自动生文', '自动生图并存图', '图片随对话一起保存'],
  capabilities: MULTIMODAL_CAPABILITIES,
};

const VOICE_PLAN: PlanDefinition = {
  id: 'voice',
  name: '声图文对话套餐',
  tagline: '生文、生图、朗读一条龙',
  priceLabel: '¥ --',
  priceNote: '/ 月 · 占位',
  highlights: ['自动生文', '自动生图并存图', 'TTS 朗读 + 全自动语音对话'],
  capabilities: VOICE_CAPABILITIES,
};

/** 三档套餐，key 为档位标识。 */
export const PLANS: Readonly<Record<PlanTier, PlanDefinition>> = {
  text: TEXT_PLAN,
  multimodal: MULTIMODAL_PLAN,
  voice: VOICE_PLAN,
};

/** 展示顺序（低到高）。 */
export const PLAN_ORDER: readonly PlanTier[] = ['text', 'multimodal', 'voice'];

/** 是否为合法档位标识（用于归一化外部数据）。 */
export function isPlanTier(value: unknown): value is PlanTier {
  return value === 'text' || value === 'multimodal' || value === 'voice';
}

/** 按展示顺序列出全部套餐（返回浅拷贝，调用方改不动内部定义）。 */
export function listPlans(): PlanDefinition[] {
  return PLAN_ORDER.map((tier) => PLANS[tier]);
}

/** 取某一档定义；非法档位直接抛错，避免静默降级。 */
export function getPlan(tier: PlanTier): PlanDefinition {
  return PLANS[tier];
}

/** 取某一档的能力矩阵（深拷贝，外部改不动）。 */
export function capabilitiesOf(tier: PlanTier): PlanCapabilities {
  return { ...PLANS[tier].capabilities };
}

/** 判断某一档是否具备某项能力。 */
export function planSupports(tier: PlanTier, capability: PlanCapabilityKey): boolean {
  return PLANS[tier].capabilities[capability];
}

/** 该档具备的能力键（保持 PLAN_CAPABILITY_KEYS 顺序）。 */
export function supportedCapabilities(tier: PlanTier): PlanCapabilityKey[] {
  return PLAN_CAPABILITY_KEYS.filter((key) => PLANS[tier].capabilities[key]);
}

/** 能力矩阵的一句话摘要，用于卡片副标题 / 无障碍标签。 */
export function capabilitySummary(tier: PlanTier): string {
  return supportedCapabilities(tier)
    .map((key) => PLAN_CAPABILITY_LABELS[key])
    .join(' · ');
}

/** 档位的中文名。 */
export function planName(tier: PlanTier): string {
  return PLANS[tier].name;
}

/** 套餐由低到高的位次（用于「是否够用」判断，越小越基础）。 */
export function planRank(tier: PlanTier): number {
  return PLAN_ORDER.indexOf(tier);
}